
import { useMemo, useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';

import { ErrorBanner } from '../../components/ErrorBanner';
import { Button, Card, CardBody, CardHead } from '../../components/ui';
import { UPLOAD_CHUNK_SIZE, useUploadInserts, validateUploadBatch } from '../../hooks';
import { MAX_UPLOAD_FILES, isTaskFinished } from '../../types/api';
import { plural } from '../tts/format';
import { formatBytes } from './format';
import styles from './LibraryPage.module.css';

const VISIBLE_ROWS = 100;

interface Props {
  onClose: () => void;
  canEdit: boolean;
}

function enrichNotice(result: unknown): string {
  const enrich = (result as { enrich?: { ok?: number; failed?: number; skipped_reason?: string } })
    ?.enrich;
  if (!enrich) return 'Готово.';
  if (enrich.skipped_reason) {
    return `Файлы в библиотеке, но разметка не прошла: ${enrich.skipped_reason}. Разметить можно кнопкой.`;
  }
  const ok = enrich.ok ?? 0;
  const failed = enrich.failed ?? 0;
  if (failed > 0) {
    return `Размечено ${ok}, не удалось ${failed}. Эти поиском по смыслу не находятся.`;
  }
  return `Готово, размечено ${ok}.`;
}

const OUTCOME_LABELS: Readonly<Record<string, string>> = {
  imported: 'залито',
  repaired: 'перезалито',
  exists: 'уже в библиотеке',
  duplicate: 'дубль в пачке',
  planned: 'предпросмотр, не записано',
  missing_file: 'файл не найден',
  failed: 'ошибка',
};

const GOOD_OUTCOMES = new Set(['imported', 'repaired']);
const BAD_OUTCOMES = new Set(['failed', 'missing_file']);

const STAGE_CLASS = {
  idle: styles.stage_idle,
  run: styles.stage_run,
  ok: styles.stage_ok,
  err: styles.stage_err,
} as const;

const BAR_CLASS = {
  idle: styles.fileBarIdle,
  run: styles.fileBarRun,
  ok: styles.fileBarOk,
  err: styles.fileBarErr,
} as const;

interface ResultRow {
  title: string;
  outcome: string;
  reason: string | null;
}

export function UploadPanel({ onClose, canEdit }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { upload, task, sentFiles, totalFiles, rows } = useUploadInserts();

  const problems = useMemo(() => validateUploadBatch(files), [files]);
  const sending = upload.isPending;
  const parsing = task.isRunning;
  const busy = sending || parsing;
  const finished = isTaskFinished(task.task.data?.status);

  const byStem = useMemo(() => {
    const map = new Map<string, ResultRow>();
    for (const row of rows) map.set(row.title, row);
    return map;
  }, [rows]);

  function addFiles(incoming: FileList | null) {
    if (!incoming) return;
    setFiles((current) => {
      const merged = [...current];
      for (const file of Array.from(incoming)) {
        if (!merged.some((existing) => existing.name === file.name && existing.size === file.size)) {
          merged.push(file);
        }
      }
      return merged;
    });
  }

  function submit() {
    if (problems.length > 0 || busy || !canEdit) return;
    upload.mutate({
      files,
    });
  }

  function startOver() {
    setFiles([]);
    upload.reset();
    task.reset();
  }

  const stageOf = (file: File): { text: string; tone: 'idle' | 'run' | 'ok' | 'err' } => {
    if (finished) {
      const stem = file.name.replace(/\.[^.]+$/, '');
      const row = byStem.get(stem);
      if (!row) return { text: 'нет результата', tone: 'err' };
      const label = OUTCOME_LABELS[row.outcome] ?? row.outcome;
      if (BAD_OUTCOMES.has(row.outcome)) {
        return { text: row.reason ? `${label}: ${row.reason}` : label, tone: 'err' };
      }
      return { text: label, tone: GOOD_OUTCOMES.has(row.outcome) ? 'ok' : 'idle' };
    }
    if (parsing) return { text: 'разбирается', tone: 'run' };
    if (sending) return { text: 'передаётся', tone: 'run' };
    return { text: 'в очереди', tone: 'idle' };
  };

  const fileRows = files.map((file, index) => ({ file, index, stage: stageOf(file) }));
  const shown =
    fileRows.length <= VISIBLE_ROWS
      ? fileRows
      : [
          ...fileRows.slice(0, VISIBLE_ROWS),
          ...fileRows.slice(VISIBLE_ROWS).filter((row) => row.stage.tone === 'err'),
        ];
  const hidden = fileRows.length - shown.length;

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const taskLost = task.task.isError && task.task.error?.isNotFound;

  return (
    <Card>
      <CardHead
        title={
          files.length > 0
            ? `Загрузка пачки · ${files.length} из ${MAX_UPLOAD_FILES} · ${formatBytes(totalBytes)}`
            : 'Загрузка пачки'
        }
        hint="теги, настроение и гео проставятся сами"
        actions={
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label="Закрыть панель загрузки"
          >
            <X size={13} strokeWidth={1.8} />
          </Button>
        }
      />

      <CardBody pad className={styles.panelBody}>
        {!canEdit && (
          <div className={styles.notice}>Роль «наблюдатель» не загружает материалы.</div>
        )}

        <div
          className={`${styles.dropzone} ${dragging ? styles.dropzoneOver : ''}`}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            if (canEdit && !busy) addFiles(event.dataTransfer.files);
          }}
        >
          <Upload size={18} strokeWidth={1.4} />
          <div className={styles.dropRow}>
            Перетащите файлы сюда или
            <Button
              variant="ghost"
              size="xs"
              className={styles.linkButton}
              onClick={() => inputRef.current?.click()}
              disabled={!canEdit || busy}
            >
              выберите на диске
            </Button>
          </div>
          <div className={styles.panelHint}>
            до {MAX_UPLOAD_FILES} файлов за раз, каждый не больше 512 МБ
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              addFiles(event.target.files);
              event.target.value = '';
            }}
          />
        </div>


        {files.length > 0 && (
          <>
            <ul className={styles.fileList}>
              {shown.map(({ file, index, stage }) => (
                <li key={`${index}:${file.name}:${file.size}`} className={styles.fileRow}>
                  <span className={styles.fileName} title={file.name}>
                    {file.name}
                  </span>
                  <span className={styles.fileSize}>{formatBytes(file.size)}</span>
                  <span className={styles.fileBar}>
                    <span className={BAR_CLASS[stage.tone]} />
                  </span>
                  <span className={STAGE_CLASS[stage.tone]}>{stage.text}</span>
                  {!busy && !finished && (
                    <Button
                      variant="ghost"
                      size="xs"
                      iconOnly
                      onClick={() => setFiles((list) => list.filter((item) => item !== file))}
                      aria-label={`Убрать ${file.name} из пачки`}
                    >
                      <X size={12} strokeWidth={1.8} />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            {hidden > 0 && (
              <div className={styles.panelHint}>
                и ещё {hidden} {plural(hidden, 'файл', 'файла', 'файлов')}. Проблемные показываются
                все.
              </div>
            )}
          </>
        )}

        {problems.length > 0 && files.length > 0 && (
          <div className={styles.problems} role="alert">
            {problems.map((problem) => (
              <div key={problem}>{problem}</div>
            ))}
          </div>
        )}

        {upload.error && <ErrorBanner error={upload.error} />}
        {task.task.error && !taskLost && <ErrorBanner error={task.task.error} />}
        {taskLost && (
          <div className={styles.notice}>
            Статус разбора потерян. Файлы записываются по мере разбора: обновите
            библиотеку и проверьте, что появилось.
          </div>
        )}

        {sending && totalFiles > UPLOAD_CHUNK_SIZE && (
          <div className={styles.notice}>
            Отправлено {sentFiles} из {totalFiles}. Идём частями по {UPLOAD_CHUNK_SIZE}: если
            связь оборвётся, уже отправленное останется.
          </div>
        )}

        {parsing && (
          <div className={styles.notice}>
            Пачка принята ({upload.data?.accepted_files ?? files.length}{' '}
            {upload.data?.accepted_files === 1 ? 'файл' : 'файлов'}), идёт разбор. Экран
            можно закрыть, работа не прервётся.
          </div>
        )}

        {finished && task.task.data?.status === 'failed' && (
          <div className={styles.problems} role="alert">
            {task.task.data.error ?? 'Разбор не удался'}
          </div>
        )}

        {finished && task.task.data?.status === 'succeeded' && (
          <div className={styles.notice}>{enrichNotice(task.task.data.result)}</div>
        )}

        <div className={styles.panelActions}>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!canEdit || busy || files.length === 0 || problems.length > 0}
          >
            {sending
              ? 'Передаём…'
              : parsing
                ? 'Разбираем…'
                : `Загрузить ${files.length || ''}`.trim()}
          </Button>
          {(finished || upload.isError) && <Button onClick={startOver}>Новая пачка</Button>}
          {!busy && files.length > 0 && !finished && (
            <Button onClick={() => setFiles([])}>Очистить список</Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
